"use client";

import React, {
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  useTranslation,
} from "react-i18next";

import {
  useNavigate,
} from "react-router-dom";

import {
  useDispatch,
  useSelector,
} from "react-redux";
import { Plus } from "lucide-react";

import "./PatientProfileCard.css";

import { usePatientProfile } from "../../../../Profile/hooks/usePatientProfile";

import { addMedication, fetchMedications } from "../../../../medications/redux/thunks/medicationThunks";
import { addBmiEntry, fetchBmiHistoryByUserId } from "../../../../Profile/redux/thunks/bmiHistoryThunks";
import { addPersonalHistory, fetchPersonalHistoryByUserId } from "../../../../Profile/redux/thunks/personalHistoryThunks";
import { addFamilyHistory, fetchFamilyHistory } from "../../../../Profile/redux/thunks/familyHistoryThunks";
import { addSurgicalHistory, fetchSurgicalHistory } from "../../../../Profile/redux/thunks/surgicalHistoryThunks";
import { addVaccination, fetchVaccinations } from "../../../../Profile/redux/thunks/vaccinationThunks";
import AddMedicationModal from "../../../../medications/components/AddModal/AddModal";

import {
  selectActiveMedications,
  selectMedicationState,
} from "../../../../medications/redux/slices/medicationSlice";

import { DOCTOR_ROUTES } from "../../../../routes/patientRoutes/routePaths";

import PatientChevronDownIcon from "../../../../assets/icons/svg/filepage-patient-chevron-down.svg";
import PatientChevronUpIcon from "../../../../assets/icons/svg/filepage-patient-chevron-up.svg";
import { resolvePatientPublicIdentifier } from "./patientPublicIdentifier";

/* =========================================================
   HELPERS
========================================================= */

const EMPTY_ARRAY = [];

const normalizeArray = (value) => {
  if (Array.isArray(value)) {
    return value;
  }

  if (Array.isArray(value?.data)) {
    return value.data;
  }

  if (Array.isArray(value?.items)) {
    return value.items;
  }

  if (Array.isArray(value?.results)) {
    return value.results;
  }

  return EMPTY_ARRAY;
};

const normalizeObject = (value) => {
  if (!value) {
    return {};
  }

  if (Array.isArray(value)) {
    return value[0] || {};
  }

  if (
    value.data &&
    !Array.isArray(value.data)
  ) {
    return value.data;
  }

  return value;
};

/* =========================================================
   INITIALS
========================================================= */

const getInitials = ({
  firstName,
  lastName,
  fullName,
  email,
}) => {
  const sourceName =
    fullName ||
    `${firstName || ""} ${
      lastName || ""
    }`.trim() ||
    email ||
    "?";

  const words =
    String(sourceName)
      .trim()
      .split(/\s+/)
      .filter(Boolean);

  if (!words.length) {
    return "?";
  }

  const first =
    words[0]?.[0] || "";

  const last =
    words.length > 1
      ? words[
          words.length - 1
        ]?.[0] || ""
      : "";

  return (
    `${first}${last}`.toUpperCase() ||
    "?"
  );
};

/* =========================================================
   DEFAULT AVATAR
========================================================= */

const isDefaultAvatarUrl = (url) => {
  const value =
    String(url || "")
      .trim()
      .toLowerCase();

  if (!value) {
    return true;
  }

  return (
    value.includes(
      "cdn-icons-png.flaticon.com/512/149/149071.png"
    ) ||
    value.includes(
      "default-avatar"
    )
  );
};

/* =========================================================
   FORMAT MEASUREMENT
========================================================= */

const formatMeasurement = (
  value,
  unit
) => {
  if (
    value === undefined ||
    value === null ||
    value === ""
  ) {
    return "—";
  }

  const stringValue =
    String(value).trim();

  /*
   * La valeur contient déjà une unité :
   * "86 kg", "163 cm"
   */
  if (
    /[a-zA-Z]/.test(
      stringValue
    )
  ) {
    return stringValue;
  }

  const normalizedNumber =
    Number(
      stringValue.replace(
        ",",
        "."
      )
    );

  if (
    Number.isFinite(
      normalizedNumber
    )
  ) {
    return `${
      Number.isInteger(
        normalizedNumber
      )
        ? normalizedNumber
        : Number(
            normalizedNumber.toFixed(
              2
            )
          )
    } ${unit}`;
  }

  return stringValue;
};

/* =========================================================
   DATE
========================================================= */

const formatDateDisplay = (
  value
) => {
  if (!value) {
    return null;
  }

  const date =
    new Date(value);

  if (
    Number.isNaN(
      date.getTime()
    )
  ) {
    return String(value);
  }

  const day =
    String(
      date.getDate()
    ).padStart(
      2,
      "0"
    );

  const month =
    String(
      date.getMonth() + 1
    ).padStart(
      2,
      "0"
    );

  return `${day}-${month}-${date.getFullYear()}`;
};

/* =========================================================
   PHONE
========================================================= */

const formatPhone = (
  value
) => {
  if (!value) {
    return "—";
  }

  const phone =
    String(value).trim();

  if (!phone) {
    return "—";
  }

  return phone.startsWith("+")
    ? phone
    : `+${phone}`;
};

/* =========================================================
   ACCORDION
========================================================= */

function AccordionSection({
  title,
  count,
  isOpen,
  onToggle,
  onBlurCapture,
  variant = "default",
  onTitleClick,
  onAdd,
  addLabel,
  children,
}) {
  const { t } = useTranslation();
  const sectionClass =
    variant === "danger"
      ? "ppc-section ppc-section--danger"
      : "ppc-section";

  return (
    <div
      className={
        sectionClass
      }
      onBlurCapture={onBlurCapture}
    >
      <div className="ppc-sectionHead">
        <div
          className={`ppc-sectionHeadLeft${
            onTitleClick
              ? " ppc-sectionHeadLeft--nav"
              : ""
          }`}
          onClick={
            onTitleClick ||
            undefined
          }
          role={
            onTitleClick
              ? "button"
              : undefined
          }
          tabIndex={
            onTitleClick
              ? 0
              : undefined
          }
          onKeyDown={
            onTitleClick
              ? (event) => {
                  if (
                    event.key ===
                      "Enter" ||
                    event.key ===
                      " "
                  ) {
                    event.preventDefault();

                    onTitleClick();
                  }
                }
              : undefined
          }
        >
          <span className="ppc-sectionTitle">
            {title}
          </span>

          {typeof count ===
            "number" && (
            <span
              className={
                variant ===
                "danger"
                  ? "ppc-sectionBadge ppc-sectionBadge--danger"
                  : "ppc-sectionBadge"
              }
            >
              {count}
            </span>
          )}
        </div>

        <div className="ppc-sectionHeadActions">
          {onAdd && (
            <button
              type="button"
              className="ppc-sectionAdd"
              onClick={onAdd}
              aria-label={addLabel}
              title={addLabel}
            >
              <Plus size={15} />
            </button>
          )}
          <button
            type="button"
            className="ppc-sectionChevron"
            onClick={onToggle}
            aria-label={t("patientProfil.toggleSection")}
          >
            <img
              src={isOpen ? PatientChevronUpIcon : PatientChevronDownIcon}
              alt=""
              aria-hidden="true"
              width="16"
              height="16"
            />
          </button>
        </div>
      </div>

      {isOpen && (
        <div className="ppc-sectionBody">
          {children}
        </div>
      )}
    </div>
  );
}

/* =========================================================
   COMPONENT
========================================================= */

export default function PatientProfileCard({
  patientData,
  safePatientId,
  canNavigate = true,
  medicalAccessCapabilities = {},
  medicalAccessStatus = "loading",
  medicalAccessLoading = true,
  onOpenNephrologyProfile,
}) {
  const {
    t,
  } = useTranslation();

  const navigate =
    useNavigate();

  const isLocalOwner = medicalAccessStatus === "local-owner";
  const canReadMedicalInformation = Boolean(
    isLocalOwner || medicalAccessCapabilities?.medicalInformation?.read
  );
  const canReadMedications = Boolean(
    isLocalOwner || medicalAccessCapabilities?.medications?.read
  );
  const canLoadMedicalData =
    !medicalAccessLoading && canReadMedicalInformation;

  const dispatch =
    useDispatch();

  /* =======================================================
     PROFILE DATA

     ✅ Toute la card consomme maintenant profileData.
  ======================================================= */

  const {
    userId,
    profileData,
    medicalData,
    personalHistory,
    familyData,
    surgicalData,
    vaccinationData,
    isLoading,
  } =
    usePatientProfile(
      patientData,
      { enabled: canLoadMedicalData }
    );

  /* =======================================================
     MEDICATIONS
  ======================================================= */

  const medicationsFromStore =
    useSelector(
      selectActiveMedications
    );

  const medicationState =
    useSelector(
      selectMedicationState
    );

  useEffect(() => {
    if (!userId || medicalAccessLoading || !canReadMedications) {
      return;
    }

    dispatch(
      fetchMedications({
        userId,
      })
    );
  }, [
    dispatch,
    userId,
    medicalAccessLoading,
    canReadMedications,
  ]);

  /* =======================================================
     AVATAR
  ======================================================= */

  const [
    avatarLoadError,
    setAvatarLoadError,
  ] = useState(false);

  const [isEditingMeasurements, setIsEditingMeasurements] = useState(false);
  const [measurementDraft, setMeasurementDraft] = useState({ height: "", weight: "" });
  const [isSavingMeasurements, setIsSavingMeasurements] = useState(false);
  const [measurementError, setMeasurementError] = useState("");
  const [activeAddSection, setActiveAddSection] = useState(null);
  const [isMedicationModalOpen, setIsMedicationModalOpen] = useState(false);
  const [isSavingSection, setIsSavingSection] = useState(false);
  const [sectionFormError, setSectionFormError] = useState("");
  const [pendingSectionEntries, setPendingSectionEntries] = useState([]);
  const [sectionDraft, setSectionDraft] = useState({
    item: "", reaction: "", name: "", dosage: "",
    time: "08:00", type: "SMOKING", value: "", relation: "",
    condition: "", vaccine: "", date: new Date().toISOString().slice(0, 10),
  });

  useEffect(() => {
    setAvatarLoadError(
      false
    );
  }, [
    profileData?.photo,
    userId,
  ]);

  /* =======================================================
     ACCORDION
  ======================================================= */

  const [
    open,
    setOpen,
  ] = useState({
    demographic: true,
    allergies: true,
    treatments: true,
    personalHistory: true,
    vaccinations: false,
    family: true,
  });

  const toggle = (
    key
  ) => {
    setOpen(
      (previous) => ({
        ...previous,

        [key]:
          !previous[key],
      })
    );
  };

  /* =======================================================
     ROUTES
  ======================================================= */

  const patientRouteId =
    safePatientId ||
    userId ||
    patientData?.id;

  const baseRoutes =
    useMemo(() => {
      if (
        !canNavigate ||
        !patientRouteId
      ) {
        return {
          overviews: "",
          medicines: "",
        };
      }

      const base =
        `/doctor/patients/${patientRouteId}`;

      return {
        overviews:
          `${base}/DoctorOverviewsPage`,

        medicines:
          `${base}/DoctorMedicinesPage`,
      };
    }, [
      canNavigate,
      patientRouteId,
    ]);

  /* =======================================================
     MEDICAL
  ======================================================= */

  const medical =
    useMemo(
      () =>
        normalizeObject(
          medicalData
        ),
      [medicalData]
    );

  /* =======================================================
     PERSONAL HISTORY
  ======================================================= */

  const personalItems =
    useMemo(() => {
      return normalizeArray(
        personalHistory
      )
        .filter((item) => {
          const type =
            String(
              item?.type || ""
            ).toUpperCase();

          /*
           * Une allergie ne doit pas apparaître
           * également dans Personal Medical History.
           */
          return (
            type !==
            "ALLERGY"
          );
        })
        .map(
          (
            item,
            index
          ) => ({
            id:
              item?.id ||
              item?._id ||
              `personal-${index}`,

            label:
              item?.label ||
              item?.condition ||
              item?.name ||
              item?.type ||
              "Unknown",

            level:
              item?.value ||
              item?.status ||
              item?.severity ||
              item?.level ||
              "",
          })
        );
    }, [
      personalHistory,
    ]);

  /* =======================================================
     ALLERGIES

     ✅ Important :
     on combine :
     - medicalData.allergies
     - surgicalData avec type ALLERGY
     - personalHistory avec type ALLERGY
  ======================================================= */

  const allergyItems =
    useMemo(() => {
      const collected = [];

      /* ---------- Medical ---------- */

      const medicalAllergies =
        normalizeArray(
          medical?.allergies ||
            medical?.allergie
        );

      medicalAllergies.forEach(
        (
          allergy,
          index
        ) => {
          if (
            typeof allergy ===
            "string"
          ) {
            collected.push({
              id:
                `medical-allergy-${index}`,

              name:
                allergy,

              severity:
                "",
            });

            return;
          }

          collected.push({
            id:
              allergy?.id ||
              allergy?._id ||
              `medical-allergy-${index}`,

            name:
              allergy?.item ||
              allergy?.allergen ||
              allergy?.name ||
              allergy?.label ||
              "Unknown",

            severity:
              allergy?.reaction ||
              allergy?.severity ||
              allergy?.level ||
              "",
          });
        }
      );

      /* ---------- Surgical history ---------- */

      normalizeArray(
        surgicalData
      )
        .filter(
          (item) =>
            String(
              item?.type ||
                item?.category ||
                ""
            ).toUpperCase() ===
            "ALLERGY"
        )
        .forEach(
          (
            allergy,
            index
          ) => {
            collected.push({
              id:
                allergy?.id ||
                allergy?._id ||
                `surgical-allergy-${index}`,

              name:
                allergy?.item ||
                allergy?.allergen ||
                allergy?.name ||
                allergy?.condition ||
                allergy?.label ||
                "Unknown",

              severity:
                allergy?.reaction ||
                allergy?.severity ||
                allergy?.status ||
                "",
            });
          }
        );

      /* ---------- Personal history ---------- */

      normalizeArray(
        personalHistory
      )
        .filter(
          (item) =>
            String(
              item?.type ||
                item?.category ||
                ""
            ).toUpperCase() ===
            "ALLERGY"
        )
        .forEach(
          (
            allergy,
            index
          ) => {
            collected.push({
              id:
                allergy?.id ||
                allergy?._id ||
                `personal-allergy-${index}`,

              name:
                allergy?.item ||
                allergy?.allergen ||
                allergy?.name ||
                allergy?.condition ||
                allergy?.label ||
                allergy?.value ||
                "Unknown",

              severity:
                allergy?.reaction ||
                allergy?.severity ||
                allergy?.status ||
                "",
            });
          }
        );

      /* ---------- Deduplication ---------- */

      const map =
        new Map();

      collected.forEach(
        (allergy) => {
          const key =
            String(
              allergy.name ||
                allergy.id
            )
              .trim()
              .toLowerCase();

          if (
            !map.has(key)
          ) {
            map.set(
              key,
              allergy
            );
          }
        }
      );

      return Array.from(
        map.values()
      );
    }, [
      medical,
      surgicalData,
      personalHistory,
    ]);

  /* =======================================================
     VACCINATIONS
  ======================================================= */

  const vaccinationItems =
    useMemo(() => {
      return normalizeArray(
        vaccinationData
      ).map(
        (
          vaccination,
          index
        ) => ({
          id:
            vaccination?.id ||
            vaccination?._id ||
            `vaccination-${index}`,

          label:
            vaccination?.vaccine ||
            vaccination?.name ||
            vaccination?.label ||
            vaccination?.vaccin ||
            "Unknown",

          date:
            vaccination?.date ||
            vaccination?.administeredDate ||
            vaccination?.vaccinationDate ||
            vaccination?.createdAt ||
            null,
        })
      );
    }, [
      vaccinationData,
    ]);

  /* =======================================================
     FAMILY HISTORY
  ======================================================= */

  const familyItems =
    useMemo(() => {
      return normalizeArray(
        familyData
      ).map(
        (
          item,
          index
        ) => ({
          id:
            item?.id ||
            item?._id ||
            `family-${index}`,

          relation:
            item?.relation ||
            item?.relationship ||
            item?.member ||
            "Unknown",

          condition:
            item?.condition ||
            item?.disease ||
            item?.pathology ||
            item?.pathologie ||
            item?.name ||
            "Unknown",
        })
      );
    }, [
      familyData,
    ]);

  /* =======================================================
     MEDICATIONS
  ======================================================= */

  const medicationItems =
    useMemo(() => {
      if (
        !Array.isArray(
          medicationsFromStore
        )
      ) {
        return [];
      }

      return medicationsFromStore.map(
        (
          medication,
          index
        ) => {
          if (
            typeof medication ===
            "string"
          ) {
            return {
              id:
                `medication-${index}`,

              name:
                medication,
            };
          }

          return {
            id:
              medication?.id ||
              medication?._id ||
              `medication-${index}`,

            name:
              medication?.name ||
              medication?.medicationName ||
              medication?.drugName ||
              medication?.medicineName ||
              medication?.text ||
              medication?.medication?.name ||
              "—",
          };
        }
      );
    }, [
      medicationsFromStore,
    ]);

  /* =======================================================
     DEMOGRAPHIC
  ======================================================= */

  const demographicItems =
    useMemo(() => {
      if (!profileData) {
        return [];
      }

      return [
        {
          key:
            "gender",

          label:
            t(
              "patientProfil.labels.gender",
              {
                defaultValue:
                  "Gender",
              }
            ),

          value:
            profileData?.gender ||
            "—",
        },

        {
          key:
            "height",

          label:
            t(
              "patientProfil.labels.height",
              {
                defaultValue:
                  "Height",
              }
            ),

          value:
            formatMeasurement(
              profileData?.height,
              "cm"
            ),
        },

        {
          key:
            "weight",

          label:
            t(
              "patientProfil.labels.weight",
              {
                defaultValue:
                  "Weight",
              }
            ),

          value:
            formatMeasurement(
              profileData?.weight,
              "kg"
            ),
        },

        {
          key:
            "age",

          label:
            t(
              "patientProfil.labels.age",
              {
                defaultValue:
                  "Age",
              }
            ),

          value:
            profileData?.age !==
              null &&
            profileData?.age !==
              undefined
              ? `${profileData.age}`
              : "—",
        },
      ];
    }, [
      profileData,
      t,
    ]);

  const startEditingMeasurements = () => {
    if (isEditingMeasurements || isSavingMeasurements) return;
    setMeasurementDraft({
      height: String(profileData?.height ?? "").replace(/\s*cm$/i, ""),
      weight: String(profileData?.weight ?? "").replace(/\s*kg$/i, ""),
    });
    setMeasurementError("");
    setIsEditingMeasurements(true);
  };

  const saveMeasurementsOnBlur = async (event) => {
    if (event.currentTarget.contains(event.relatedTarget)) return;
    if (!isEditingMeasurements || isSavingMeasurements) return;

    const height = Number(String(measurementDraft.height).trim().replace(",", "."));
    const weight = Number(String(measurementDraft.weight).trim().replace(",", "."));
    const initialHeight = Number(String(profileData?.height ?? "").replace(/\s*cm$/i, "").replace(",", "."));
    const initialWeight = Number(String(profileData?.weight ?? "").replace(/\s*kg$/i, "").replace(",", "."));

    if (
      !Number.isFinite(height) || height <= 0 ||
      !Number.isFinite(weight) || weight <= 0
    ) {
      setMeasurementError(t("patientProfil.measurements.invalid"));
      return;
    }

    if (height === initialHeight && weight === initialWeight) {
      setIsEditingMeasurements(false);
      return;
    }

    setIsSavingMeasurements(true);
    setMeasurementError("");
    try {
      await dispatch(addBmiEntry({
        userId,
        values: { tailleCm: height, poidsKg: weight },
      })).unwrap();
      await dispatch(fetchBmiHistoryByUserId(userId));
      setIsEditingMeasurements(false);
    } catch {
      setMeasurementError(t("patientProfil.measurements.saveFailed"));
    } finally {
      setIsSavingMeasurements(false);
    }
  };

  const openAddSection = (section) => {
    setActiveAddSection(section);
    setSectionFormError("");
    setPendingSectionEntries([]);
    setOpen((previous) => ({ ...previous, [section]: true }));
  };

  const updateSectionDraft = (field, value) => {
    setSectionDraft((previous) => ({ ...previous, [field]: value }));
  };

  const resetSectionDraft = () => setSectionDraft({
    item: "", reaction: "", name: "", dosage: "",
    time: "08:00", type: "SMOKING", value: "", relation: "",
    condition: "", vaccine: "", date: new Date().toISOString().slice(0, 10),
  });

  const stageSectionEntry = (event) => {
    event.preventDefault();
    if (activeAddSection === "allergies" && !sectionDraft.item.trim()) {
      setSectionFormError(t("patientProfil.addInfo.required"));
      return;
    }
    if (activeAddSection === "treatments" && !sectionDraft.name.trim()) {
      setSectionFormError(t("patientProfil.addInfo.required"));
      return;
    }
    if (activeAddSection === "personalHistory" && !sectionDraft.type.trim()) {
      setSectionFormError(t("patientProfil.addInfo.required"));
      return;
    }
    if (activeAddSection === "vaccinations" && !sectionDraft.vaccine.trim()) {
      setSectionFormError(t("patientProfil.addInfo.required"));
      return;
    }
    if (activeAddSection === "family" && (!sectionDraft.relation.trim() || !sectionDraft.condition.trim())) {
      setSectionFormError(t("patientProfil.addInfo.required"));
      return;
    }

    setPendingSectionEntries((previous) => [...previous, { ...sectionDraft }]);
    setSectionFormError("");
    resetSectionDraft();
  };

  const saveSectionEntries = async () => {
    if (!userId || !activeAddSection || !pendingSectionEntries.length || isSavingSection) return;
    setIsSavingSection(true);
    setSectionFormError("");

    try {
      for (const entry of pendingSectionEntries) {
        if (activeAddSection === "allergies") {
          await dispatch(addSurgicalHistory({ userId, values: {
            type: "ALLERGY", item: entry.item.trim(), reaction: entry.reaction.trim(),
          } })).unwrap();
        } else if (activeAddSection === "treatments") {
          await dispatch(addMedication({ userId, medicationData: {
            name: entry.name.trim(),
            ...(entry.dosage ? { dosage: Number(entry.dosage) } : {}),
            amount: 1,
            times: [entry.time || "08:00"],
            startDate: new Date().toISOString().slice(0, 10),
            tracking: { mode: "daily", selectedDays: [] },
          } })).unwrap();
        } else if (activeAddSection === "personalHistory") {
          await dispatch(addPersonalHistory({ userId, values: {
            type: entry.type.trim(), value: entry.value.trim(),
          } })).unwrap();
        } else if (activeAddSection === "vaccinations") {
          await dispatch(addVaccination({ userId, values: {
            vaccine: entry.vaccine.trim(), date: entry.date,
          } })).unwrap();
        } else if (activeAddSection === "family") {
          await dispatch(addFamilyHistory({ userId, values: {
            relation: entry.relation.trim(), condition: entry.condition.trim(),
          } })).unwrap();
        }
      }

      if (activeAddSection === "allergies") await dispatch(fetchSurgicalHistory(userId));
      if (activeAddSection === "treatments") await dispatch(fetchMedications({ userId })).unwrap();
      if (activeAddSection === "personalHistory") await dispatch(fetchPersonalHistoryByUserId(userId));
      if (activeAddSection === "vaccinations") await dispatch(fetchVaccinations(userId));
      if (activeAddSection === "family") await dispatch(fetchFamilyHistory(userId));

      setActiveAddSection(null);
      setPendingSectionEntries([]);
      resetSectionDraft();
    } catch (error) {
      setSectionFormError(
        typeof error === "string" ? error : error?.message || t("patientProfil.addInfo.failed")
      );
    } finally {
      setIsSavingSection(false);
    }
  };

  const renderAddForm = (section) => {
    if (activeAddSection !== section) return null;
    const field = (name, label, type = "text", placeholder = "") => (
      <label className="ppc-addField" key={name}>
        <span>{t(label)}</span>
        <input
          type={type}
          value={sectionDraft[name]}
          onChange={(event) => updateSectionDraft(name, event.target.value)}
          placeholder={placeholder ? t(placeholder) : undefined}
          disabled={isSavingSection}
        />
      </label>
    );

    return (
      <form className="ppc-addForm" onSubmit={stageSectionEntry}>
        {pendingSectionEntries.length > 0 && (
          <ul className="ppc-pendingEntries">
            {pendingSectionEntries.map((entry, index) => (
              <li key={`${activeAddSection}-${index}`}>
                {section === "allergies" && `${entry.item}${entry.reaction ? ` (${entry.reaction})` : ""}`}
                {section === "treatments" && `${entry.name}${entry.dosage ? ` · ${entry.dosage} mg` : ""}`}
                {section === "personalHistory" && `${entry.type}${entry.value ? ` · ${entry.value}` : ""}`}
                {section === "vaccinations" && `${entry.vaccine} · ${entry.date}`}
                {section === "family" && `${entry.relation} · ${entry.condition}`}
                <button type="button" onClick={() => setPendingSectionEntries((previous) => previous.filter((_, rowIndex) => rowIndex !== index))} aria-label={t("patientProfil.addInfo.removeEntry")}>
                  ×
                </button>
              </li>
            ))}
          </ul>
        )}
        <div className="ppc-addFields">
        {section === "allergies" && (
          <>
            {field("item", "patientProfil.addInfo.allergen")}
            {field("reaction", "patientProfil.addInfo.reaction")}
          </>
        )}
        {section === "treatments" && (
          <>
            {field("name", "patientProfil.addInfo.medication")}
            {field("dosage", "patientProfil.addInfo.dosage", "number")}
            {field("time", "patientProfil.addInfo.firstDoseTime", "time")}
          </>
        )}
        {section === "personalHistory" && (
          <>
            {field("type", "patientProfil.addInfo.historyType")}
            {field("value", "patientProfil.addInfo.details")}
          </>
        )}
        {section === "vaccinations" && (
          <>
            {field("vaccine", "patientProfil.addInfo.vaccine")}
            {field("date", "patientProfil.addInfo.date", "date")}
          </>
        )}
        {section === "family" && (
          <>
            {field("relation", "patientProfil.addInfo.relation")}
            {field("condition", "patientProfil.addInfo.condition")}
          </>
        )}
        <button type="submit" className="ppc-addStage" aria-label={t("patientProfil.addInfo.addEntry")} title={t("patientProfil.addInfo.addEntry")} disabled={isSavingSection}>
          <Plus size={15} />
        </button>
        </div>
        {sectionFormError && <div className="ppc-addError" role="alert">{sectionFormError}</div>}
        <div className="ppc-addActions">
          <button type="button" className="ppc-addCancel" onClick={() => { setActiveAddSection(null); setPendingSectionEntries([]); setSectionFormError(""); resetSectionDraft(); }} disabled={isSavingSection}>
            {t("common.cancel")}
          </button>
          <button type="button" className="ppc-addSave" onClick={saveSectionEntries} disabled={isSavingSection || pendingSectionEntries.length === 0}>
            {isSavingSection ? t("patientProfil.addInfo.saving") : t("common.save")}
          </button>
        </div>
      </form>
    );
  };

  /* =======================================================
     LOADING
  ======================================================= */

  if (
    medicalAccessLoading ||
    (isLoading && !profileData?.id)
  ) {
    return (
      <div className="ppc-card">
        <div className="ppc-loading">
          <div className="ppc-spinner" />

          <p>
            {t(
              "patientProfil.loading",
              {
                defaultValue:
                  "Loading...",
              }
            )}
          </p>
        </div>
      </div>
    );
  }

  /* =======================================================
     AVATAR
  ======================================================= */

  const profilePhoto =
    profileData?.photo &&
    !isDefaultAvatarUrl(
      profileData.photo
    )
      ? profileData.photo
      : null;

  const patientBadgeValue =
    resolvePatientPublicIdentifier(
      profileData,
      patientData
    ) ||
    "—";

  /* =======================================================
     RENDER
  ======================================================= */

  return (
    <div className="ppc-card">

      {/* ===============================================
          HEADER
      =============================================== */}

      <div className="ppc-header">
        <div className="ppc-idBadge">
          {patientBadgeValue}
        </div>

        {profilePhoto &&
        !avatarLoadError ? (
          <img
            src={
              profilePhoto
            }
            alt={
              profileData?.fullName ||
              t("patientProfil.avatarAlt")
            }
            className="ppc-avatar"
            onError={() =>
              setAvatarLoadError(
                true
              )
            }
          />
        ) : (
          <div className="ppc-avatar-fallback">
            {getInitials({
              firstName:
                profileData?.firstName,

              lastName:
                profileData?.lastName,

              fullName:
                profileData?.fullName,

              email:
                profileData?.email,
            })}
          </div>
        )}

        <div className="ppc-headerText">
          <h3 className="ppc-name">
            {profileData?.fullName ||
              "—"}
          </h3>

          <p className="ppc-phone">
            {formatPhone(
              profileData?.phone
            )}
          </p>

          <p className="ppc-email">
            {profileData?.email ||
              "—"}
          </p>
        </div>
      </div>

      {/* ===============================================
          VIEW PROFILE
      =============================================== */}

      <button
        type="button"
        className="ppc-viewProfileBtn"
        disabled={!userId}
        onClick={() => {
          if (!userId) {
            return;
          }

          navigate(
            DOCTOR_ROUTES.DOCTOR_PATIENT_VIEW(
              userId
            )
          );
        }}
      >
        {t(
          "patientProfil.viewProfile",
          {
            defaultValue:
              "View Profile",
          }
        )}
      </button>

      {!canReadMedicalInformation && !canReadMedications && (
        <div className="ppc-accessNotice" role="status">
          {t("doctorOverviews.access.title", {
            defaultValue: "Patient access is required to view this information.",
          })}
        </div>
      )}

      {/* ===============================================
          GENERAL INFORMATION
      =============================================== */}

      {canReadMedicalInformation && (
      <AccordionSection
        title={t(
          "patientProfil.sections.demographic",
          {
            defaultValue:
              "General Information",
          }
        )}
        count={
          demographicItems.length
        }
        isOpen={
          open.demographic
        }
        onToggle={() =>
          toggle(
            "demographic"
          )
        }
        onBlurCapture={saveMeasurementsOnBlur}
        onTitleClick={
          baseRoutes.overviews
            ? () => navigate(baseRoutes.overviews, {
                state: {
                  patientData,
                  startEditing: true,
                },
              })
            : undefined
        }
      >
        <ul
          className={`ppc-list ppc-demographicList ${isEditingMeasurements ? "is-editing" : ""}`}
          onClick={startEditingMeasurements}
        >
          {demographicItems.map(
            (item) => (
              <li
                key={
                  item.key
                }
                className="ppc-listItem"
              >
                <span className="ppc-dot" />

                <span className="ppc-listItemText">
                  {item.label}{" : "}
                  {isEditingMeasurements && item.key === "height" ? (
                    <input
                      autoFocus
                      className="ppc-measurementInput"
                      type="number"
                      min="1"
                      step="0.1"
                      aria-label={item.label}
                      value={measurementDraft.height}
                      onChange={(event) => setMeasurementDraft((previous) => ({ ...previous, height: event.target.value }))}
                      disabled={isSavingMeasurements}
                    />
                  ) : isEditingMeasurements && item.key === "weight" ? (
                    <input
                      className="ppc-measurementInput"
                      type="number"
                      min="1"
                      step="0.1"
                      aria-label={item.label}
                      value={measurementDraft.weight}
                      onChange={(event) => setMeasurementDraft((previous) => ({ ...previous, weight: event.target.value }))}
                      disabled={isSavingMeasurements}
                    />
                  ) : item.value}
                  {isEditingMeasurements && (item.key === "height" || item.key === "weight") && (
                    <span className="ppc-measurementUnit">{item.key === "height" ? "cm" : "kg"}</span>
                  )}
                </span>
              </li>
            )
          )}
        </ul>
        {measurementError && (
          <div className="ppc-measurementError" role="alert">{measurementError}</div>
        )}
        {isSavingMeasurements && (
          <div className="ppc-measurementSaving">{t("patientProfil.measurements.saving")}</div>
        )}
      </AccordionSection>
        )}

      {/* ===============================================
          ALLERGIES
      =============================================== */}

        {canReadMedicalInformation && (
      <AccordionSection
        variant="danger"
        onAdd={activeAddSection !== "allergies" ? () => openAddSection("allergies") : undefined}
        addLabel={t("patientProfil.addInfo.addAllergy")}
        title={t(
          "patientProfil.sections.allergies",
          {
            defaultValue:
              "Allergies",
          }
        )}
        count={
          allergyItems.length
        }
        isOpen={
          open.allergies
        }
        onToggle={() =>
          toggle(
            "allergies"
          )
        }
      >
        {allergyItems.length >
        0 ? (
          <ul className="ppc-list">
            {allergyItems.map(
              (allergy) => (
                <li
                  key={
                    allergy.id
                  }
                  className="ppc-listItem"
                >
                  <span className="ppc-dot ppc-dot--danger" />

                  <span className="ppc-listItemText">
                    {allergy.name}

                    {allergy.severity
                      ? ` (${allergy.severity})`
                      : ""}
                  </span>
                </li>
              )
            )}
          </ul>
        ) : (
          <div className="ppc-muted">
            {t(
              "patientProfil.noData",
              {
                defaultValue:
                  "No data",
              }
            )}
          </div>
        )}
        {renderAddForm("allergies")}
      </AccordionSection>
        )}

      {/* ===============================================
          CURRENT TREATMENTS
      =============================================== */}

        {canReadMedications && (
      <AccordionSection
        onAdd={() => setIsMedicationModalOpen(true)}
        addLabel={t("patientProfil.addInfo.addMedication")}
        title={t(
          "patientProfil.sections.treatments",
          {
            defaultValue:
              "Current Treatments",
          }
        )}
        count={
          medicationItems.length
        }
        isOpen={
          open.treatments
        }
        onToggle={() =>
          toggle(
            "treatments"
          )
        }
        onTitleClick={
          baseRoutes.medicines
            ? () =>
                navigate(
                  baseRoutes.medicines
                )
            : undefined
        }
      >
        {medicationState?.loading ? (
          <div className="ppc-muted">
            {t(
              "patientProfil.loading",
              {
                defaultValue:
                  "Loading...",
              }
            )}
          </div>
        ) : medicationItems.length >
          0 ? (
          <ul className="ppc-list">
            {medicationItems.map(
              (
                medication
              ) => (
                <li
                  key={
                    medication.id
                  }
                  className="ppc-listItem"
                >
                  <span className="ppc-dot" />

                  <span className="ppc-listItemText">
                    {medication.name}
                  </span>
                </li>
              )
            )}
          </ul>
        ) : (
          <div className="ppc-muted">
            {t(
              "patientProfil.noData",
              {
                defaultValue:
                  "No data",
              }
            )}
          </div>
        )}
      </AccordionSection>
        )}

      {/* ===============================================
          PERSONAL MEDICAL HISTORY
      =============================================== */}

        {canReadMedicalInformation && (
        <>
      <AccordionSection
        onAdd={activeAddSection !== "personalHistory" ? () => openAddSection("personalHistory") : undefined}
        addLabel={t("patientProfil.addInfo.addPersonalHistory")}
        title={t(
          "patientProfil.sections.personalHistory",
          {
            defaultValue:
              "Personal Medical History",
          }
        )}
        count={
          personalItems.length
        }
        isOpen={
          open.personalHistory
        }
        onToggle={() =>
          toggle(
            "personalHistory"
          )
        }
        onTitleClick={
          baseRoutes.overviews
            ? () =>
                navigate(
                  baseRoutes.overviews
                )
            : undefined
        }
      >
        {personalItems.length >
        0 ? (
          <ul className="ppc-list">
            {personalItems.map(
              (item) => (
                <li
                  key={
                    item.id
                  }
                  className="ppc-listItem"
                >
                  <span className="ppc-dot" />

                  <span className="ppc-listItemText">
                    {item.label}

                    {item.level
                      ? ` (${item.level})`
                      : ""}
                  </span>
                </li>
              )
            )}
          </ul>
        ) : (
          <div className="ppc-muted">
            {t(
              "patientProfil.noData",
              {
                defaultValue:
                  "No data",
              }
            )}
          </div>
        )}
        {renderAddForm("personalHistory")}
      </AccordionSection>

      {/* ===============================================
          VACCINATIONS
      =============================================== */}

      <AccordionSection
        onAdd={activeAddSection !== "vaccinations" ? () => openAddSection("vaccinations") : undefined}
        addLabel={t("patientProfil.addInfo.addVaccination")}
        title={t(
          "patientProfil.sections.vaccinations",
          {
            defaultValue:
              "Vaccinations",
          }
        )}
        count={
          vaccinationItems.length
        }
        isOpen={
          open.vaccinations
        }
        onToggle={() =>
          toggle(
            "vaccinations"
          )
        }
      >
        {vaccinationItems.length >
        0 ? (
          <ul className="ppc-list">
            {vaccinationItems.map(
              (
                vaccination
              ) => (
                <li
                  key={
                    vaccination.id
                  }
                  className="ppc-listItem"
                >
                  <span className="ppc-dot" />

                  <span className="ppc-listItemText">
                    {
                      vaccination.label
                    }

                    {vaccination.date
                      ? ` : ${
                          formatDateDisplay(
                            vaccination.date
                          ) ||
                          "—"
                        }`
                      : ""}
                  </span>
                </li>
              )
            )}
          </ul>
        ) : (
          <div className="ppc-muted">
            {t(
              "patientProfil.noData",
              {
                defaultValue:
                  "No data",
              }
            )}
          </div>
        )}
        {renderAddForm("vaccinations")}
      </AccordionSection>

      {/* ===============================================
          FAMILY MEDICAL HISTORY

          ✅ Dernière card.
          ✅ Pas de Emergency card.
      =============================================== */}

      <AccordionSection
        onAdd={activeAddSection !== "family" ? () => openAddSection("family") : undefined}
        addLabel={t("patientProfil.addInfo.addFamilyHistory")}
        title={t(
          "patientProfil.sections.family",
          {
            defaultValue:
              "Family Medical History",
          }
        )}
        count={
          familyItems.length
        }
        isOpen={
          open.family
        }
        onToggle={() =>
          toggle(
            "family"
          )
        }
      >
        {familyItems.length >
        0 ? (
          <ul className="ppc-list">
            {familyItems.map(
              (
                familyItem
              ) => (
                <li
                  key={
                    familyItem.id
                  }
                  className="ppc-listItem"
                >
                  <span className="ppc-dot" />

                  <span className="ppc-listItemText">
                    {
                      familyItem.relation
                    }
                    {" : "}
                    {
                      familyItem.condition
                    }
                  </span>
                </li>
              )
            )}
          </ul>
        ) : (
          <div className="ppc-muted">
            {t(
              "patientProfil.noData",
              {
                defaultValue:
                  "No data",
              }
            )}
          </div>
        )}
        {renderAddForm("family")}
      </AccordionSection>
        </>
      )}

      <div className="ppc-footerAction">
        <button
          type="button"
          className="ppc-editNephrologyBtn"
          onClick={() => onOpenNephrologyProfile?.()}
        >
          {t("patientProfil.editNephrologyProfile", {
            defaultValue: "Edit Nephrology Profile",
          })}
        </button>
      </div>

      <AddMedicationModal
        open={isMedicationModalOpen}
        onClose={() => setIsMedicationModalOpen(false)}
        userId={userId}
        doctorMode
        onCreateMedication={() => {
          if (userId) dispatch(fetchMedications({ userId }));
        }}
      />
    </div>
  );
}
